import {
  buildChartCalculationPayload,
  CHART_CALCULATION_TIMEOUT_MS,
} from '@dbl/services/common/chartCalculationCore';
import { datalabHttp, datalabWithTokenHttp, type TDatalabHttpConfig } from '@dbl/services/common';
import type { IAnalysisChartRequest } from '@dbl/services/chart';

import { transformPreviewSqlResponseDtoToVo } from './transformers';
import type {
  ICheckCustomSqlRequest,
  ICheckSqlAndOperationRequest,
  IPreviewSqlVo,
} from './types';

/**
 * 根据当前分析条件预览最终 SQL（`analysis_preview_sql`）。
 *
 * 原始实现：`src/pages/datalab/services/analysis.ts` -> getPreviewSQL
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/Multidimensional/Analysis/model.ts`
 */
export const getPreviewSQL = (
  param: IAnalysisChartRequest,
): Promise<IPreviewSqlVo> =>
  datalabWithTokenHttp({
    url: '/api/v1/datalab/analysis_preview_sql',
    method: 'post',
    data: buildChartCalculationPayload(param),
    timeout: CHART_CALCULATION_TIMEOUT_MS,
  } as TDatalabHttpConfig).then(transformPreviewSqlResponseDtoToVo);

/**
 * 校验拖拽分析中的自定义 SQL **表达式片段**（`analysis_check_custom_sql`）。
 *
 * 与 {@link checkSqlAndOperation} 的区别：
 * - 本接口面向自定义图表字段里的 `expression` 文本，不绑定数据库/schema；
 * - 用于分析配置阶段校验表达式语法，不执行查询、不返回结果集。
 *
 * 原始实现：`src/pages/datalab/services/analysis.ts` -> AnalysisCheckCustomSql
 */
export const checkCustomSql = (
  param: ICheckCustomSqlRequest,
): Promise<unknown> =>
  datalabHttp({
    url: '/api/v1/datalab/analysis_check_custom_sql',
    method: 'post',
    data: param,
  });

/**
 * 自定义 SQL 虚拟表：校验或试运行完整 SQL（`customSql/exec`）。
 *
 * 与 {@link checkCustomSql} 的区别：
 * - 本接口面向虚拟表建表编辑器中的**完整 SELECT**，需 `database_uuid`、`schema`、`operate`；
 * - `operate` 为 `check` 时仅校验，`run` 时试运行并返回预览数据。
 *
 * 原始实现：`src/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/service.ts`
 * 原始引用：`HeaderData/components/RightSqlEdit/index.tsx`
 */
export const checkSqlAndOperation = (
  param: ICheckSqlAndOperationRequest,
): Promise<unknown> =>
  datalabHttp({
    url: '/api/v1/datalab/customSql/exec',
    method: 'post',
    data: param,
  });
